<template>
    <Modal
        v-if="modelValue"
        :width="640"
        :model-value="modelValue"
        :title="modalTitle"
        @update:modelValue="emit('update:modelValue', $event)"
    >
        <div v-if="isLoading" class="preflight-loading">
            <Icon type="md-spinner" spin />
            <span>Loading preflight reports...</span>
        </div>

        <div v-else class="preflight-modal-content">
            <div v-if="preflightHistory.length === 0" class="preflight-no-results">
                <p>No preflight reports available.</p>
            </div>

            <template v-else>
                <PreflightPreview
                    :key="preflightResultsIndex"
                    :preflight-history="preflightHistory[preflightResultsIndex - 1]"
                    :platform="platform"
                />

                <Page
                    v-if="preflightHistory.length > 1"
                    v-model="preflightResultsIndex"
                    class="preflight-pagination"
                    size="small"
                    :page-size="1"
                    :total="preflightHistory.length"
                />
            </template>
        </div>
        <template #footer>
            <Button type="primary" data-testid="preflight-modal-close-button" @click="emit('update:modelValue', false)">
                Close
            </Button>
        </template>
    </Modal>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, onUnmounted } from "vue";
import { Modal, Button, Page } from "view-ui-plus";
import { PLATFORM } from "hoxton-shared/enums/exports";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

import { getPreflightHistory, type PreflightHistory } from "@/utils/Preflight/getPreflightHistory";
import PreflightPreview from "./PreflightPreview.vue";

const props = defineProps<{
    modelValue: boolean;
    deliverableId: string;
    platform: typeof PLATFORM.CREATIVEX | typeof PLATFORM.SATALIA;
}>();

const emit = defineEmits<(e: "update:modelValue", value: boolean) => void>();
const { trackFeatureEvent } = useGA4Tracking();

const isLoading = ref(true);
const preflightHistory = ref<PreflightHistory[]>([]);
const preflightResultsIndex = ref(1);

const modalTitle = computed(() => {
    return props.platform === PLATFORM.CREATIVEX ? "CreativeX preflight reports" : "Pre-flight reports";
});

onMounted(async () => {
    const outputs = await getPreflightHistory(props.deliverableId, props.platform);
    trackFeatureEvent({
        featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
        featureCategory: GA4.FEATURE_CATEGORIES.PREFLIGHT,
        featureContext: GA4.FEATURE_CONTEXTS.PREFLIGHT_REPORT,
        freeformValues: [modalTitle.value]
    });
    preflightHistory.value = outputs;

    isLoading.value = false;
});

onUnmounted(() => {
    trackFeatureEvent({
        featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
        featureCategory: GA4.FEATURE_CATEGORIES.PREFLIGHT,
        featureContext: GA4.FEATURE_CONTEXTS.PREFLIGHT_REPORT,
        freeformValues: [modalTitle.value]
    });
});
</script>

<style scoped>
.preflight-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
}

.preflight-no-results {
    text-align: center;
    padding: 20px;
}

.preflight-modal-content {
    display: flex;
    flex-direction: column;

    gap: 32px;
}

.preflight-pagination {
    margin-left: auto;
}
</style>
